export const SYSTEM_CHECK_MINUTES_BEFORE_EXAM_DEFAULT = 10;
export const SYSTEM_CHECK_MINUTES_BEFORE_EXAM_MAX = 180;

/**
 * Normalize client-provided minutes-before value.
 * Accepts minutes (10) or accidental milliseconds (600000 → 10).
 * Clamps to 1..180 so a bad prop cannot open the env check days early.
 */
export function normalizeSystemCheckMinutesBefore(value) {
  if (value == null || value === '') return SYSTEM_CHECK_MINUTES_BEFORE_EXAM_DEFAULT;
  let n = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isFinite(n) || n <= 0) return SYSTEM_CHECK_MINUTES_BEFORE_EXAM_DEFAULT;
  // Likely milliseconds (e.g. 10 * 60 * 1000)
  if (n >= 1000) {
    n = Math.round(n / 60000);
  } else if (n > SYSTEM_CHECK_MINUTES_BEFORE_EXAM_MAX) {
    // Accidental seconds (e.g. 10 * 60 = 600)
    n = Math.round(n / 60);
  }
  n = Math.round(n);
  if (n < 1) return 1;
  if (n > SYSTEM_CHECK_MINUTES_BEFORE_EXAM_MAX) return SYSTEM_CHECK_MINUTES_BEFORE_EXAM_MAX;
  return n;
}

export function parseExamTimestamp(value) {
  if (value == null || value === '') return null;
  try {
    if (typeof value === 'number') {
      // Unix seconds (e.g. 1720960000) vs milliseconds (e.g. 1720960000000)
      const ms = value < 1e12 ? value * 1000 : value;
      return Number.isFinite(ms) ? ms : null;
    }
    const str = String(value).trim();
    // Pure numeric string (Unix seconds or ms)
    if (/^\d+$/.test(str)) {
      const n = Number(str);
      if (!Number.isFinite(n)) return null;
      const ms = n < 1e12 ? n * 1000 : n;
      return Number.isFinite(ms) ? ms : null;
    }
    // Timezone-naive datetime (MySQL / LMS wall clock) — always local, never UTC-by-default.
    // Prevents T-N window shifting by the browser's UTC offset (common "env check never opens" bug).
    const hasTz = /([zZ]|[+-]\d{2}:?\d{2})$/.test(str);
    const naive = str.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/
    );
    if (naive && !hasTz) {
      const year = Number(naive[1]);
      const month = Number(naive[2]) - 1;
      const day = Number(naive[3]);
      const hour = Number(naive[4] || 0);
      const minute = Number(naive[5] || 0);
      const second = Number(naive[6] || 0);
      const ms = new Date(year, month, day, hour, minute, second).getTime();
      return Number.isFinite(ms) ? ms : null;
    }
    const ts = Date.parse(str);
    return Number.isFinite(ts) ? ts : null;
  } catch (_) {
    return null;
  }
}

export function getSystemCheckWindowStart(examStartAt, minutesBefore = SYSTEM_CHECK_MINUTES_BEFORE_EXAM_DEFAULT) {
  const startTs = parseExamTimestamp(examStartAt);
  if (startTs == null) return null;
  const minutes = normalizeSystemCheckMinutesBefore(minutesBefore);
  return startTs - minutes * 60 * 1000;
}

export function isWithinSystemCheckWindow(examStartAt, minutesBefore = SYSTEM_CHECK_MINUTES_BEFORE_EXAM_DEFAULT, now = Date.now()) {
  const windowStart = getSystemCheckWindowStart(examStartAt, minutesBefore);
  const startTs = parseExamTimestamp(examStartAt);
  if (windowStart == null || startTs == null) return false;
  return now >= windowStart && now < startTs;
}

/**
 * True from T-N minutes before exam start through exam end (until endExam).
 * Used to open the environment check for registered users who have not completed it yet.
 * Before T-N this is always false (strict — no early env check).
 *
 * Callers that sync server time should pass a conservative `now` (e.g. Math.min(client, server))
 * so a fast server clock cannot open the env check before the real T-N window.
 */
export function isEnvironmentCheckPeriod(
  examStartAt,
  endExam = false,
  minutesBefore = SYSTEM_CHECK_MINUTES_BEFORE_EXAM_DEFAULT,
  now = Date.now()
) {
  if (endExam) return false;
  const startTs = parseExamTimestamp(examStartAt);
  if (startTs == null) return false;
  const windowStart = getSystemCheckWindowStart(examStartAt, minutesBefore);
  if (windowStart == null) return false;
  // Strict: never before T-N. From T-N through exam until endExam.
  return now >= windowStart;
}

/**
 * Prefer the lagging clock when deciding whether the T-N window has opened.
 * Prevents a ahead-of-real-time server offset from showing the env check early.
 */
export function conservativeNowMs(serverAdjustedNow = Date.now(), clientNow = Date.now()) {
  const a = Number(serverAdjustedNow);
  const b = Number(clientNow);
  if (!Number.isFinite(a)) return Number.isFinite(b) ? b : Date.now();
  if (!Number.isFinite(b)) return a;
  return Math.min(a, b);
}

/**
 * True once the pre-exam window has opened (T-N), through exam start, until endExam.
 * Used to keep the WebSocket connected from env-check through exam end.
 */
export function isProctoringSessionPeriod(
  examStartAt,
  endExam = false,
  minutesBefore = SYSTEM_CHECK_MINUTES_BEFORE_EXAM_DEFAULT,
  now = Date.now()
) {
  if (endExam) return false;
  const windowStart = getSystemCheckWindowStart(examStartAt, minutesBefore);
  if (windowStart == null) return false;
  return now >= windowStart;
}

export function msUntilSystemCheckWindow(examStartAt, minutesBefore = SYSTEM_CHECK_MINUTES_BEFORE_EXAM_DEFAULT, now = Date.now()) {
  const windowStart = getSystemCheckWindowStart(examStartAt, minutesBefore);
  if (windowStart == null) return null;
  return windowStart - now;
}

export function formatExamTimestamp(value) {
  const ts = parseExamTimestamp(value);
  if (ts == null) return null;
  try {
    return new Date(ts).toISOString();
  } catch (_) {
    return String(ts);
  }
}

export function hasExamStarted(examStartAt, now = Date.now()) {
  const startTs = parseExamTimestamp(examStartAt);
  if (startTs == null) return null;
  return now >= startTs;
}

/**
 * When closed-book preflight and enforcement should run.
 * Live if the LMS marks the exam active, start is true, or examStartAt has been reached.
 * If there is no schedule signal at all, treat the host page as already in-exam (typical LMS mount).
 */
export function isClosedBookLaunchPeriod({
  examIsActive,
  start,
  examStartAt,
  endExam = false,
  systemCheckMinutesBeforeExam = SYSTEM_CHECK_MINUTES_BEFORE_EXAM_DEFAULT,
  now = Date.now(),
} = {}) {
  if (endExam) return false;
  if (examIsActive === true || start === true) return true;
  // Same window as environment check: force from T-N through the exam.
  if (isEnvironmentCheckPeriod(examStartAt, endExam, systemCheckMinutesBeforeExam, now)) return true;
  const started = hasExamStarted(examStartAt, now);
  if (started === true) return true;
  if (started === false) return false;
  return true;
}

export function describeExamSchedule(
  examStartAt,
  endExam = false,
  minutesBefore = SYSTEM_CHECK_MINUTES_BEFORE_EXAM_DEFAULT,
  now = Date.now()
) {
  const minutes = normalizeSystemCheckMinutesBefore(minutesBefore);
  const startTs = parseExamTimestamp(examStartAt);
  const windowStart = getSystemCheckWindowStart(examStartAt, minutes);
  const msUntil = msUntilSystemCheckWindow(examStartAt, minutes, now);
  const inWindow = isEnvironmentCheckPeriod(examStartAt, endExam, minutes, now);
  const sessionOpen = isProctoringSessionPeriod(examStartAt, endExam, minutes, now);
  const started = hasExamStarted(examStartAt, now);

  return {
    raw: { examStartAt, endExam, systemCheckMinutesBeforeExam: minutesBefore },
    parsed: {
      examStartAt: startTs,
      examStartAtIso: formatExamTimestamp(examStartAt),
      systemCheckMinutesBeforeExam: minutes,
    },
    window: {
      minutesBefore: minutes,
      windowStartTs: windowStart,
      windowStartIso: windowStart != null ? new Date(windowStart).toISOString() : null,
      examStartIso: formatExamTimestamp(examStartAt),
      nowIso: new Date(now).toISOString(),
      msUntilWindow: msUntil,
      minutesUntilWindow: msUntil != null ? Math.round(msUntil / 60000) : null,
      minutesUntilExamStart: startTs != null ? Math.round((startTs - now) / 60000) : null,
      examHasStarted: started,
      inEnvironmentCheckPeriod: inWindow,
      proctoringSessionOpen: sessionOpen,
      endExam: !!endExam,
      monitoringStopped: !!endExam,
    },
  };
}
